// The web map (BL-018, the maps design v3, package 4): Google's map in Blueprint's style, light or dark
// as the page is, with our own drawing on top: the visits by state in their technician's pen, each
// technician's route straight from pin to pin, the shop, and each technician's dot while on duty. The
// drawing is DayMap's, laid out on the map's own projection (map-view.ts), so the Schedule's map
// (package 5) hands it the same answer.
//
// With the browser's key (the session's mapsKey) it loads the Maps JavaScript API once a page
// (map-loader.ts). Without one, or when Google refuses the key or can't be reached, it draws a stand-in:
// the same drawing on a plain ground in the map's colours, a grid a round distance square and a scale,
// and it says it is one. With the company's maps off (the session's mapsOff, QA-756) it says that, and
// offers Settings to someone who may turn them on. Tests and development use the stand-in; nothing calls
// Google from a test.
//
// Google's terms allow its points on Google's map only (3.2.3(e)): every pin says where its point came
// from, and the stand-in draws only ours (a person's pin, the iPad's arrival, the import's), never one
// Google placed, nor a route through one; it says so.
//
// It follows the theme it's given (the office passes useShownTheme()), restyling Google's map in place.
// Dragging it moves the map, never the page. Everything on it has a name for a screen reader: the map,
// each pin (its visit's title), the shop and each technician's dot.
// Reads only --googlemap-* (GoogleMap.css; was base.css's, BL-189); Link reads its own.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { MapsApi, MapsFailure, MapsLoader } from '../../map-loader.ts';
import { pageMapsLoader } from '../../map-loader.ts';
import { liveMap, type LiveMap, type MapView } from '../../map-live.ts';
import type { GoogleMapData } from '../../map-schemas.ts';
import type { MapTheme } from '../../map-style.ts';
import { fitPoints, fitView, overlayLayout, scaleBar, type Project } from '../../map-view.ts';
import { Link } from '../Link/Link.tsx';
import { penStyle as pens, type Pen } from '../pen.ts';
import './GoogleMap.css';

/** A pin's, a route's or a technician's pen; none (nobody, or waiting) is red (--googlemap-bad). */
const penStyle = (pen: number | null | undefined): CSSProperties =>
  pens('googlemap', (pen ?? null) as Pen | null) ??
  ({ '--googlemap-pen': 'var(--googlemap-bad)' } as CSSProperties);

/** Each size's height (GoogleMap.css .flw-gmap-large / -small, --googlemap-large-h / -small-h), and the width drawn before the page measures it. */
export const MAP_HEIGHTS = { large: 420, small: 220 } as const;
const FIRST_WIDTH = 720;
const r1 = (n: number) => Math.round(n * 10) / 10;

/** What the stand-in says of itself, first under the map; a refused or unreachable map in red. */
export const STAND_IN_WORDS: Record<'none' | 'off' | MapsFailure, string> = {
  none: 'A stand-in: no map key is set, so the pins are on a plain grid, not a street map.',
  // the company has maps off (maps.google, QA-756): a choice, not a fault
  off: 'Maps are off for this company, so the pins are on a plain grid.',
  refused: 'Google refused the map key, so this is a stand-in. The key’s list of sites needs this address.',
  unreachable: 'Google Maps couldn’t be reached, so this is a stand-in.',
};
/** With maps off, who turns them on: the link for someone who may, the words for everyone else. */
export const MAPS_OFF_WORDS = {
  link: 'Turn on in Settings',
  ask: 'An owner can turn them on in Settings.',
} as const;
/** Said under a stand-in that leaves Google's points off: some of them, or all it had. */
export const GOOGLE_ONLY_WORDS = {
  some: 'Pins Google placed show only on Google’s map, so the stand-in leaves them off.',
  all: 'No pin to show without the map: Google placed it, and its points show only on Google’s map.',
} as const;
/** What the Schedule's map says its marks mean, under it. */
export const VISITS_LEGEND =
  'A filled pin is working now, an outline is booked, a muted one is done, red is at risk or has no one yet. A line is a technician’s route, in their pen, and a dot with an arrow is where they are now.';

/** What the stand-in may draw: our own points only, and each route through them alone. */
export function oursOnly(d: GoogleMapData): GoogleMapData {
  const pins = d.pins.filter((x) => x.source !== 'google');
  if (pins.length === d.pins.length) return d;
  const kept = new Set(pins.map((x) => x.id));
  return { ...d, pins, routes: d.routes.map((r) => ({ ...r, stops: r.stops.filter((s) => kept.has(s)) })) };
}

export interface GoogleMapProps {
  data: GoogleMapData;
  /** What the map is, for a screen reader: "Today’s visits on the map". */
  label: string;
  /** Light or dark, as the page draws it now; the map follows a change. */
  theme: MapTheme;
  /** The browser's key (the session's mapsKey); none draws the stand-in. */
  mapsKey?: string | null;
  /**
   * Why there's no key (the session's mapsOff): `company` while the company has maps off, which the
   * stand-in says; `server` or none for no key at all.
   */
  mapsOff?: 'company' | 'server' | null;
  /** With maps off for the company, where they're turned on, for someone who may (settings.edit). */
  turnOn?: { href: string; onOpen?: () => void } | null;
  /** The visit picked, drawn over the rest with its name. */
  selected?: string | null;
  /** With it, each pin is a button that picks its visit; without, the pins are pictures. */
  onPick?: (id: string) => void;
  /** The Schedule's map, or an address's. */
  size?: keyof typeof MAP_HEIGHTS;
  /** Where to look when there's no point at all. */
  center?: { lat: number; lng: number } | null;
  /** The loader, for a test or the gallery; the page's own by default, none on a server render. */
  loader?: MapsLoader | null;
  /** What the marks mean, under the map; the visits' by default, none for an address on its own. */
  legend?: string | null;
}

type Shown = 'loading' | 'live' | 'none' | 'off' | MapsFailure;

export function GoogleMap(p: GoogleMapProps) {
  const size = p.size ?? 'large';
  const key = p.mapsKey?.trim() || null;
  const loader = p.loader === undefined ? pageMapsLoader() : p.loader;
  // no key: the company's maps are off, or there's none at all
  const keyless: Shown = !key && p.mapsOff === 'company' ? 'off' : 'none';
  const [shown, setShown] = useState<Shown>(() =>
    !key || !loader ? keyless : loader.refused() ? 'refused' : 'loading',
  );
  const [api, setApi] = useState<MapsApi | null>(null);
  const [view, setView] = useState<MapView | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const live = useRef<LiveMap | null>(null);
  const [box, setBox] = useState<{ width: number; height: number }>({
    width: FIRST_WIDTH,
    height: MAP_HEIGHTS[size],
  });
  // the overlay's own box, in Google's pane; our drawing is portalled into it
  const pane = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.className = 'flw-gmap-over';
    return el;
  }, []);

  // load the API with the key, and fall back to the stand-in if Google refuses it later
  useEffect(() => {
    if (!key || !loader) {
      setShown(keyless);
      return;
    }
    let gone = false;
    setShown(loader.refused() ? 'refused' : 'loading');
    void loader.load(key).then((r) => {
      if (gone) return;
      if (r.ok) {
        setApi(() => r.api);
        setShown('live');
      } else setShown(r.why);
    });
    const stop = loader.onRefused(() => {
      if (gone) return;
      setApi(null);
      setShown('refused');
    });
    return () => {
      gone = true;
      stop();
    };
  }, [key, loader, keyless]);

  // the stand-in is drawn to the frame's measured size
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth && el.clientHeight) setBox({ width: el.clientWidth, height: el.clientHeight });
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const seen = new ResizeObserver(measure);
    seen.observe(el);
    return () => seen.disconnect();
  }, []);

  const points = fitPoints(p.data);
  const fitKey = JSON.stringify([points, p.center ?? null]);
  const fitted = useRef(fitKey);

  // Google's map, made once the API is in; taken down when it goes (a refused key, unmounting)
  const live0 = useRef({ theme: p.theme, points, center: p.center ?? null });
  live0.current = { theme: p.theme, points, center: p.center ?? null };
  useEffect(() => {
    if (shown !== 'live' || !api || !host.current || !pane) return;
    const m = liveMap(api, host.current, pane, { ...live0.current, onView: setView });
    live.current = m;
    fitted.current = fitKey;
    return () => {
      m.destroy();
      live.current = null;
      setView(null);
    };
    // fitKey and the theme are followed below, without making the map again
  }, [api, shown, pane]);
  // the drawing for the view is in: its box moves onto that view's frame before the browser paints, so
  // the pins never show on one frame's places in another's box (QA-761)
  useLayoutEffect(() => {
    if (view) live.current?.drawn(view);
  }, [view]);
  useEffect(() => {
    live.current?.setTheme(p.theme);
  }, [p.theme]);
  useEffect(() => {
    if (!live.current || fitted.current === fitKey) return;
    fitted.current = fitKey;
    live.current.fit(points, p.center ?? null);
    // points and the centre are fitKey's own
  }, [fitKey]);

  const onGoogle = shown === 'loading' || shown === 'live';
  // the stand-in isn't Google's map: Google's points stay off it, and so do routes through them
  const ours = oursOnly(p.data);
  const left = p.data.pins.length - ours.pins.length;
  const bare = left > 0 && !ours.pins.length && !ours.techs.length && !ours.shop;
  const legend = p.legend === undefined ? VISITS_LEGEND : p.legend;
  return (
    <figure className="flw-gmap-fig">
      <div
        ref={frame}
        className={`flw-gmap flw-gmap-${size}${p.theme === 'dark' ? ' flw-gmap-dark' : ''}`}
        role="region"
        aria-label={p.label}
        {...(shown === 'loading' ? { 'aria-busy': true } : {})}
      >
        {onGoogle ? (
          <div ref={host} className="flw-gmap-live" />
        ) : (
          <StandIn
            {...p}
            data={ours}
            {...(bare ? { center: null, bare } : {})}
            width={box.width}
            height={box.height}
          />
        )}
        {shown === 'live' && view && pane
          ? createPortal(
              <Drawing {...p} project={view.project} width={view.width} height={view.height} />,
              pane,
            )
          : null}
      </div>
      {onGoogle && !legend ? null : (
        <figcaption className="flw-gmap-key">
          {onGoogle ? null : (
            <span
              className={`flw-gmap-note${shown === 'none' || shown === 'off' ? '' : ' flw-gmap-bad'}`}
              role="status"
            >
              {STAND_IN_WORDS[shown]}
              {left ? ` ${GOOGLE_ONLY_WORDS[bare ? 'all' : 'some']}` : ''}
              {shown === 'off' && !p.turnOn ? ` ${MAPS_OFF_WORDS.ask}` : ''}
            </span>
          )}
          {shown === 'off' && p.turnOn ? (
            <Link href={p.turnOn.href} {...(p.turnOn.onOpen ? { onPress: p.turnOn.onOpen } : {})}>
              {MAPS_OFF_WORDS.link}
            </Link>
          ) : null}
          {legend}
        </figcaption>
      )}
    </figure>
  );
}

/** The stand-in: our drawing on a plain ground in the map's colours, a grid a round distance square, a scale. */
function StandIn(p: GoogleMapProps & { width: number; height: number; bare?: boolean }) {
  const { width: w, height: h } = p;
  const v = fitView(fitPoints(p.data), w, h, { center: p.center ?? null });
  const scale = scaleBar(v.center.lat, v.zoom, w);
  const step = scale.length;
  const lines = (span: number) =>
    Array.from(
      { length: Math.ceil(span / step) + 2 },
      (_, i) => span / 2 + (i - Math.ceil(span / step / 2)) * step,
    ).filter((n) => n > 0 && n < span);
  return (
    <>
      <svg className="flw-gmap-ground" viewBox={`0 0 ${w} ${h}`} aria-hidden="true" focusable="false">
        <rect className="flw-gmap-land" x="0" y="0" width={w} height={h} />
        <g className="flw-gmap-grid">
          {lines(w).map((x) => (
            <line key={`x${r1(x)}`} x1={r1(x)} y1="0" x2={r1(x)} y2={h} />
          ))}
          {lines(h).map((y) => (
            <line key={`y${r1(y)}`} x1="0" y1={r1(y)} x2={w} y2={r1(y)} />
          ))}
        </g>
        <g className="flw-gmap-scale" transform={`translate(12 ${h - 10})`}>
          <line x1="0" y1="0" x2={r1(step)} y2="0" />
          <line x1="0" y1="-6" x2="0" y2="0" />
          <line x1={r1(step)} y1="-6" x2={r1(step)} y2="0" />
          <text x={r1(step) + 6} y="0">
            {scale.words}
          </text>
        </g>
      </svg>
      {p.bare ? null : <Drawing {...p} project={v.project} width={w} height={h} />}
    </>
  );
}

/**
 * Our drawing on the map: routes, the shop, the technicians' dots, and the visits' pins over them, so a
 * visit under way stays readable and its pin can be picked where its technician stands.
 */
function Drawing(p: GoogleMapProps & { project: Project; width: number; height: number }) {
  const { data, selected, onPick, width: w, height: h } = p;
  const m = overlayLayout(data, p.project, w, h, selected);
  const byId = new Map(data.pins.map((x) => [x.id, x]));
  const techById = new Map(data.techs.map((t) => [t.id, t]));
  const key = (id: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onPick?.(id);
  };
  // the chosen pin last, so nothing is drawn over it
  const order = [...m.pins].sort((a, b) => Number(a.id === selected) - Number(b.id === selected));
  const places = data.pins.filter((x) => x.state === 'place').length;
  const visits = data.pins.length - places;
  const techs = data.techs.length;
  const summary = `${
    places && !visits
      ? `${places} ${places === 1 ? 'address' : 'addresses'}`
      : `${visits} ${visits === 1 ? 'visit' : 'visits'}`
  }${techs ? ` and ${techs} ${techs === 1 ? 'technician' : 'technicians'}` : ''} on the map`;
  return (
    <svg
      className="flw-gmap-ours"
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="group"
      aria-label={summary}
    >
      {m.routes.map(
        (r) =>
          r.points.length > 1 && (
            <g key={r.id} style={penStyle(r.pen)} aria-hidden="true">
              <polyline
                className="flw-gmap-route-e"
                points={r.points.map(([x, y]) => `${r1(x)},${r1(y)}`).join(' ')}
              />
              <polyline
                className="flw-gmap-route"
                points={r.points.map(([x, y]) => `${r1(x)},${r1(y)}`).join(' ')}
              />
            </g>
          ),
      )}
      {m.shop && data.shop && (
        <g
          className="flw-gmap-shop"
          transform={`translate(${r1(m.shop.x)} ${r1(m.shop.y)})`}
          role="img"
          aria-label={data.shop.label}
        >
          <rect x="-7" y="-7" width="14" height="14" />
          {m.shop.label && (
            <text x={r1(m.shop.label.x)} y={r1(m.shop.label.y)} aria-hidden="true">
              {data.shop.label}
            </text>
          )}
        </g>
      )}
      {m.techs.map((q) => {
        const t = techById.get(q.id)!;
        return (
          <g
            key={q.id}
            className={`flw-gmap-tech${t.stale ? ' flw-gmap-stale' : ''}`}
            style={penStyle(t.pen)}
            transform={`translate(${r1(q.x)} ${r1(q.y)})`}
            role="img"
            aria-label={`${q.text}: where they are`}
          >
            <circle className="flw-gmap-halo" r="20" />
            <circle className="flw-gmap-me" r="9" />
            {t.heading !== null && <path d="M0 -5 L4 4 L0 2 L-4 4Z" transform={`rotate(${r1(t.heading)})`} />}
            {q.label && (
              <text x={r1(q.label.x)} y={r1(q.label.y)} aria-hidden="true">
                {q.text}
              </text>
            )}
          </g>
        );
      })}
      {order.map((q) => {
        const pin = byId.get(q.id)!;
        const on = q.id === selected;
        return (
          <g
            key={q.id}
            className={`flw-gmap-pin flw-gmap-${pin.state}${pin.late ? ' flw-gmap-late' : ''}${on ? ' flw-gmap-on' : ''}`}
            style={penStyle(pin.state === 'waiting' ? null : pin.pen)}
            transform={`translate(${r1(q.x)} ${r1(q.y)})`}
            {...(onPick
              ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-pressed': on,
                  onClick: () => onPick(q.id),
                  onKeyDown: key(q.id),
                }
              : { role: 'img' })}
            aria-label={pin.title}
          >
            <circle r={q.r} />
            {q.label && pin.label && (
              <text x={r1(q.label.x)} y={r1(q.label.y)} aria-hidden="true">
                {pin.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
